import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

/**
 * Componente para gráfico de performance por atendente (levantadas de mão)
 */
export const AtendentesPerformanceChart = ({ atendentesData }) => {
  if (!atendentesData || atendentesData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance por Atendente</h3>
        <p className="text-gray-500">Nenhum dado de atendente disponível.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        Performance por Atendente - Levantadas de Mão
        <span className="text-sm font-normal text-gray-500 ml-2">
          (🟢 Respondidas | 🔴 Não Respondidas)
        </span>
      </h3>
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={atendentesData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="nome" 
            angle={-45}
            textAnchor="end"
            height={120}
            fontSize={11}
            interval={0}
          />
          <YAxis fontSize={12} />
          <Tooltip 
            formatter={(value, name, props) => {
              const payload = props && props.payload ? props.payload : {};
              const base = typeof payload.levantadasComMensagem === 'number' ? payload.levantadasComMensagem : 0;
              const pct = base > 0 && typeof value === 'number' ? (value / base) * 100 : 0;
              const valStr = typeof value === 'number' ? Math.round(value).toString() : String(value);
              return [`${valStr} (${pct.toFixed(1)}%)`, name];
            }}
            labelFormatter={(label) => `Atendente: ${label}`}
          />
          <Bar 
            dataKey="levantadasRespondidas" 
            fill="#10b981" 
            name="Levantadas Respondidas"
          />
          <Bar 
            dataKey="levantadasNaoRespondidas" 
            fill="#ef4444" 
            name="Levantadas Não Respondidas"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Componente para gráfico de levantadas de mão por horário
 */
export const LevantadasPorHorarioChart = ({ dadosHorarios }) => {
  if (!dadosHorarios || dadosHorarios.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Levantadas de Mão por Horário</h3>
        <p className="text-gray-500">Nenhum dado horário disponível.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Levantadas de Mão por Horário</h3>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={dadosHorarios}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="horaFormatada" 
            fontSize={12}
          />
          <YAxis fontSize={12} />
          <Tooltip 
            formatter={(value, name, props) => {
              const payload = props && props.payload ? props.payload : {};
              const total = typeof payload.totalLevantadas === 'number' ? payload.totalLevantadas : 0;
              const pct = total > 0 && typeof value === 'number' ? (value / total) * 100 : 0;
              const valStr = typeof value === 'number' ? Math.round(value).toString() : String(value);
              return [`${valStr} (${pct.toFixed(1)}%)`, name];
            }}
            labelFormatter={(hour) => `Horário: ${hour}`}
          />
          <Line 
            type="monotone" 
            dataKey="totalLevantadas" 
            stroke="#3b82f6" 
            strokeWidth={2}
            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
            name="Total de Levantadas"
          />
          <Line 
            type="monotone" 
            dataKey="levantadasComMensagem" 
            stroke="#10b981" 
            strokeWidth={2}
            dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
            name="Com Mensagem"
          />
          <Line 
            type="monotone" 
            dataKey="levantadasRespondidas" 
            stroke="#8b5cf6" 
            strokeWidth={2}
            dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
            name="Respondidas"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Componente para distribuição de tempos de resposta
 */
export const DistribuicaoTempoRespostaChart = ({ distribuicaoTempo }) => {
  if (!distribuicaoTempo || distribuicaoTempo.totalComTempo === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Distribuição de Tempos de Resposta
        </h3>
        <p className="text-gray-500">Nenhuma levantada com tempo de resposta válido encontrada.</p>
      </div>
    );
  }

  const COLORS = ['#10b981', '#f59e0b', '#ef4444'];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        Distribuição de Tempos de Resposta
        <span className="text-sm font-normal text-gray-500 ml-2">
          (Respondidas: {distribuicaoTempo.totalRespondidas} | c/ tempo válido: {distribuicaoTempo.totalComTempo})
        </span>
      </h3>
      
      {/* Gráfico de barras para faixas detalhadas */}
      <div className="mb-8">
        <h4 className="text-md font-medium text-gray-700 mb-4">Distribuição Detalhada</h4>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={distribuicaoTempo.faixasDetalhadas}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="label" 
              angle={-45}
              textAnchor="end"
              height={120}
              fontSize={10}
              interval={0}
            />
            <YAxis 
              fontSize={12}
              tickFormatter={(value) => value.toString()}
            />
            <Tooltip 
              formatter={(value, name, props) => {
                const payload = props && props.payload ? props.payload : {};
                const pct = typeof payload.percentage === 'number' ? payload.percentage : 0;
                const valStr = typeof value === 'number' ? Math.round(value).toString() : String(value);
                return [`${valStr} (${pct.toFixed(1)}%)`, name];
              }}
              labelFormatter={(label) => `Faixa: ${label}`}
            />
            <Bar 
              dataKey="count" 
              fill="#3b82f6" 
              name="Levantadas Respondidas"
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Agrupamento especial com gráfico de pizza */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-4">Agrupamento Especial</h4>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Até 60 min', value: distribuicaoTempo.agrupamentoEspecial.ate60min.count },
                  { name: '61-120 min', value: distribuicaoTempo.agrupamentoEspecial.de61a120min.count },
                  { name: '121+ min', value: distribuicaoTempo.agrupamentoEspecial.acima121min.count }
                ]}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {[0, 1, 2].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => {
                  const percent = props && props.payload && typeof props.payload.percent === 'number' 
                    ? props.payload.percent * 100 
                    : 0;
                  const valStr = typeof value === 'number' ? Math.round(value).toString() : String(value);
                  return [`${valStr} (${percent.toFixed(1)}%)`, name];
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-700 mb-4">Resumo por Categoria</h4>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="text-sm font-medium text-green-700">Até 60 minutos</div>
            <div className="text-2xl font-bold text-green-600">
              {distribuicaoTempo.agrupamentoEspecial.ate60min.count}
            </div>
            <div className="text-sm text-green-600">
              ({distribuicaoTempo.agrupamentoEspecial.ate60min.percentage.toFixed(1)}%)
            </div>
            <button
              className="mt-2 text-xs text-green-700 underline"
              onClick={() => {
                const event = new CustomEvent('open-details', {
                  detail: { type: 'tempo_resposta_faixa', faixa: 'ate60' }
                });
                window.dispatchEvent(event);
              }}
            >
              Ver registros
            </button>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <div className="text-sm font-medium text-yellow-700">De 61 a 120 minutos</div>
            <div className="text-2xl font-bold text-yellow-600">
              {distribuicaoTempo.agrupamentoEspecial.de61a120min.count}
            </div>
            <div className="text-sm text-yellow-600">
              ({distribuicaoTempo.agrupamentoEspecial.de61a120min.percentage.toFixed(1)}%)
            </div>
            <button
              className="mt-2 text-xs text-yellow-700 underline"
              onClick={() => {
                const event = new CustomEvent('open-details', {
                  detail: { type: 'tempo_resposta_faixa', faixa: '61a120' }
                });
                window.dispatchEvent(event);
              }}
            >
              Ver registros
            </button>
          </div>
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <div className="text-sm font-medium text-red-700">De 121 minutos ou mais</div>
            <div className="text-2xl font-bold text-red-600">
              {distribuicaoTempo.agrupamentoEspecial.acima121min.count}
            </div>
            <div className="text-sm text-red-600">
              ({distribuicaoTempo.agrupamentoEspecial.acima121min.percentage.toFixed(1)}%)
            </div>
            <button
              className="mt-2 text-xs text-red-700 underline"
              onClick={() => {
                const event = new CustomEvent('open-details', {
                  detail: { type: 'tempo_resposta_faixa', faixa: '121mais' }
                });
                window.dispatchEvent(event);
              }}
            >
              Ver registros
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
